import type {
  LogInput,
  LogLevel,
  LogQuery,
  LogRecord,
  LogSource,
} from '@hibro/shared';
import {
  Button,
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@hibro/ui-client';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useT } from '../i18n/i18n';

/**
 * Bridge to the main-process log sink (见 15 / §5.16). Injected by the
 * app shell (so platform-renderer stays free of the preload `window.hibroLog`
 * global), mirroring `BundleInstallBridge`. The sink lives above the ext-host so
 * it is reached over the `hibro:log:*` IPC bridge, not the ext-host Channel.
 */
export interface LogBridge {
  query(query?: LogQuery): Promise<LogRecord[]>;
  clear(): Promise<void>;
  /**
   * Record a diagnostic authored in this window. The runtime stamps `ts` and
   * `source: 'renderer'`, so a page cannot claim to be a platform process.
   *
   * The writer half of the bridge, and the only route this package has into the
   * log pipeline (见 15): it deliberately holds no reference to the
   * `window.hibroLog` global, so without this every failure it catches would have
   * to be swallowed.
   */
  append(record: LogInput): void;
  /**
   * Reveal the on-disk JSONL log folder in the OS file manager (见 15).
   *
   * Optional because it is the one part of this bridge a runtime may genuinely
   * lack: a browser has no OS folder to reveal (见 18). When absent the button is
   * not rendered at all, following the same rule as every other platform
   * capability here — not injected means not offered, rather than a control that
   * does nothing. Exporting records is unaffected: "copy as JSONL" below reads
   * the loaded rows and needs no bridge.
   */
  openFolder?(): Promise<void>;
  /**
   * Daily log files on the browser runtime (见 15). Absent on desktop, which
   * reveals the folder with {@link openFolder} instead. When either method is
   * missing the list is not rendered.
   */
  listLogFiles?(): Promise<LogFileEntry[]>;
  /** Download one file named by {@link listLogFiles}. */
  downloadLogFile?(name: string): Promise<void>;
  /** Subscribe to live appended records; returns a disposer. */
  onChanged(callback: (record: LogRecord) => void): () => void;
}

/** One durable log file the browser runtime can download (见 15). */
export interface LogFileEntry {
  name: string;
  size: number;
}

const LEVELS: LogLevel[] = ['error', 'warn', 'info', 'debug'];
const SOURCES: LogSource[] = [
  'main',
  'ext-host',
  'renderer',
  'module',
  'iframe',
  'external',
];

/** Most-recent-first cap so the diagnostics list stays bounded in the DOM. */
const MAX_ROWS = 300;

const LEVEL_COLOR: Record<LogLevel, string> = {
  error: 'text-destructive',
  warn: 'text-warning',
  info: 'text-(--app-color-text-secondary)',
  debug: 'text-(--app-color-text-faint)',
};

/**
 * One diagnostics row (见 15). Shows the compact `time level source scope message`
 * line; when the record carries structured `data` or an `error`, the row is
 * clickable to expand an indented detail block (JSON payload + error stack), so a
 * teammate can inspect a failure without leaving the panel.
 */
function LogRow({ record }: { record: LogRecord }): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = record.data !== undefined || record.error !== undefined;

  return (
    <div className="border-b border-(--app-color-border-subtle)">
      <div
        className={cn(
          'flex gap-2 px-2 py-0.5 wrap-break-word whitespace-pre-wrap',
          hasDetail && 'cursor-pointer hover:bg-accent',
        )}
        onClick={hasDetail ? () => setExpanded((prev) => !prev) : undefined}
      >
        <span className="text-(--app-color-text-faint)">
          {hasDetail ? (expanded ? '▾' : '▸') : ' '}
        </span>
        <span className="text-(--app-color-text-faint)">
          {new Date(record.ts).toLocaleTimeString()}
        </span>
        <span
          className={cn('min-w-10.5 font-semibold', LEVEL_COLOR[record.level])}
        >
          {record.level}
        </span>
        <span className="min-w-16 text-muted-foreground">
          {record.source}
          {record.kind === 'metric' ? '*' : ''}
        </span>
        {record.scope ? (
          <span className="text-(--app-color-accent)">{record.scope}</span>
        ) : null}
        <span className="flex-1">{record.message}</span>
      </div>
      {hasDetail && expanded ? (
        <div className="border-t border-(--app-color-border-subtle) bg-(--app-color-bg-subtle) px-2 py-1 text-(--app-color-text-secondary)">
          {record.data !== undefined ? (
            <pre className="m-0 overflow-x-auto whitespace-pre-wrap">
              {safeStringify(record.data)}
            </pre>
          ) : null}
          {record.error ? (
            <pre className="m-0 overflow-x-auto whitespace-pre-wrap text-destructive">
              {record.error.stack ?? record.error.message}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Stringify a structured payload for display; tolerates cycles/unstringifiables. */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Diagnostics section (见 15 / §5.16). Lists recent structured log
 * records (logs, metrics, crash reports) from every process with level/source
 * filters, live-updates as new records arrive, and offers a Clear action. Reads
 * the main-process sink through the injected {@link LogBridge}.
 */
function formatBytes(size: number): string {
  if (size < 1024) {
    return `${String(size)} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Browser-only file list. Desktop leaves both bridge methods unset, so this
 * renders nothing and the "open folder" button stays the way out (见 15).
 */
function LogFileList({ bridge }: { bridge: LogBridge }): ReactNode {
  const t = useT();
  const [files, setFiles] = useState<LogFileEntry[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [downloading, setDownloading] = useState<string | undefined>();

  const load = useCallback(() => {
    const pending = bridge.listLogFiles?.();
    if (!pending) {
      return;
    }
    pending
      .then((next) => {
        setFiles(next);
        setError(undefined);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [bridge]);

  useEffect(() => {
    load();
  }, [load]);

  if (!bridge.listLogFiles || !bridge.downloadLogFile) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="m-0 text-xs">{t('settings.diagnostics.logFiles')}</p>
      {error ? <p className="m-0 text-xs text-destructive">{error}</p> : null}
      {files && files.length === 0 && !error ? (
        <p className="m-0 text-xs text-muted-foreground">
          {t('settings.diagnostics.logFilesEmpty')}
        </p>
      ) : null}
      {files && files.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {files.map((file) => (
            <li key={file.name} className="flex items-center gap-2 text-xs">
              <span className="font-mono">{file.name}</span>
              <span className="text-muted-foreground">
                {formatBytes(file.size)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={downloading === file.name}
                onClick={() => {
                  const pending = bridge.downloadLogFile?.(file.name);
                  if (!pending) {
                    return;
                  }
                  setDownloading(file.name);
                  void pending
                    .catch((err: unknown) => {
                      setError(
                        err instanceof Error ? err.message : String(err),
                      );
                    })
                    .finally(() => {
                      setDownloading(undefined);
                    });
                }}
              >
                {t('settings.diagnostics.download')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function LogsSection({ bridge }: { bridge: LogBridge }): ReactNode {
  const t = useT();
  const [records, setRecords] = useState<LogRecord[]>([]);
  const [minLevel, setMinLevel] = useState<LogLevel | 'all'>('all');
  const [source, setSource] = useState<LogSource | 'all'>('all');
  const [text, setText] = useState('');

  const load = useCallback(() => {
    const query: LogQuery = { limit: MAX_ROWS };
    if (minLevel !== 'all') {
      query.minLevel = minLevel;
    }
    if (source !== 'all') {
      query.source = source;
    }
    const trimmed = text.trim();
    if (trimmed) {
      query.text = trimmed;
    }
    bridge
      .query(query)
      .then((next) => setRecords(next))
      .catch(() => undefined);
  }, [bridge, minLevel, source, text]);

  useEffect(() => {
    load();
  }, [load]);

  // Live-append new records (re-querying keeps filters applied) — debounced via
  // the next paint is unnecessary at this volume; just re-pull on each record.
  useEffect(() => {
    return bridge.onChanged(() => load());
  }, [bridge, load]);

  const rows = records.slice(-MAX_ROWS).reverse();

  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-xs text-muted-foreground">
        {t('settings.diagnostics.desc')}
      </p>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs">
          {t('settings.diagnostics.level')}
          <Select
            value={minLevel}
            onValueChange={(next) => {
              if (next) {
                setMinLevel(next);
              }
            }}
          >
            <SelectTrigger size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('settings.diagnostics.all')}
              </SelectItem>
              {LEVELS.map((level) => (
                <SelectItem key={level} value={level}>
                  {level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="flex items-center gap-1.5 text-xs">
          {t('settings.diagnostics.source')}
          <Select
            value={source}
            onValueChange={(next) => {
              if (next) {
                setSource(next);
              }
            }}
          >
            <SelectTrigger size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('settings.diagnostics.all')}
              </SelectItem>
              {SOURCES.map((src) => (
                <SelectItem key={src} value={src}>
                  {src}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <Input
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t('settings.diagnostics.search')}
          className="ml-auto h-7 w-44 text-xs"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const jsonl = records
              .map((record) => JSON.stringify(record))
              .join('\n');
            void navigator.clipboard
              .writeText(jsonl)
              .then(() => toast.success(t('settings.diagnostics.copied')))
              .catch(() => undefined);
          }}
        >
          {t('settings.diagnostics.copy')}
        </Button>
        {bridge.openFolder ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void bridge.openFolder?.().catch(() => undefined);
            }}
          >
            {t('settings.diagnostics.openFolder')}
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => {
            void bridge.clear().then(load);
          }}
        >
          {t('settings.diagnostics.clear')}
        </Button>
      </div>
      <LogFileList bridge={bridge} />
      {rows.length === 0 ? (
        <p className="m-0 text-xs text-muted-foreground">
          {t('settings.diagnostics.empty')}
        </p>
      ) : (
        <div className="max-h-65 overflow-y-auto rounded-md border border-(--app-color-border-subtle) font-mono text-[11px]">
          {rows.map((record, index) => (
            <LogRow
              key={`${String(record.ts)}-${String(index)}`}
              record={record}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Map a log level to the matching `console` method for the DevTools mirror. */
const CONSOLE_METHOD: Record<LogLevel, 'error' | 'warn' | 'info' | 'debug'> = {
  error: 'error',
  warn: 'warn',
  info: 'info',
  debug: 'debug',
};

/** Replay one record onto the DevTools console (grouped when it carries detail). */
function mirrorRecordToConsole(record: LogRecord): void {
  const method = CONSOLE_METHOD[record.level];
  const time = new Date(record.ts).toLocaleTimeString();
  const tag = record.scope ? `${record.source}/${record.scope}` : record.source;
  const header = `%c${time} [${tag}]%c ${record.message}`;
  const headerStyle = 'color:#888;font-weight:600';
  const hasDetail = record.data !== undefined || record.error !== undefined;
  if (!hasDetail) {
    console[method](header, headerStyle, 'color:inherit');
    return;
  }
  // groupCollapsed keeps the console tidy: header line expands to data/stack.
  console.groupCollapsed(header, headerStyle, 'color:inherit');
  if (record.data !== undefined) {
    console[method]('data', record.data);
  }
  if (record.error) {
    console[method]('error', record.error.stack ?? record.error.message);
  }
  console.groupEnd();
}

/**
 * DevTools console mirror (见 15 / §5.16). A headless listener that replays every
 * appended {@link LogRecord} from the whole pipeline (main / ext-host / renderer /
 * module / iframe / external) onto this window's DevTools console, so a teammate
 * debugging a packaged build can watch the unified log stream in the console —
 * including the main/ext-host records they otherwise could not see. Live-only (no
 * history replay); mounted by the shell only when the developer toggles it on, so
 * production is unaffected unless explicitly enabled. Does not intercept
 * `console.*`, so it cannot loop.
 */
export function LogConsoleMirror({ bridge }: { bridge: LogBridge }): ReactNode {
  useEffect(() => {
    return bridge.onChanged((record) => mirrorRecordToConsole(record));
  }, [bridge]);
  return null;
}

/** How long a crash toast stays on screen before auto-dismissing (ms). */
const CRASH_TOAST_TTL_MS = 8000;

/**
 * Crash toast (见 15 / §5.16). A headless listener on the log sink that raises a
 * shared error `toast()` (rendered by the workbench-mounted `Toaster`) for every
 * `crash`-kind record (ext-host / external / main / renderer failure). Routed
 * through the log pipeline rather than the notification EventBus so it still
 * appears when the ext-host (and thus its EventBus) is the thing that crashed.
 * Mounted persistently by the shell.
 */
export function CrashToast({ bridge }: { bridge: LogBridge }): ReactNode {
  useEffect(() => {
    return bridge.onChanged((record) => {
      if (record.kind !== 'crash') {
        return;
      }
      toast.error(record.message, {
        description: record.scope ?? record.source,
        duration: CRASH_TOAST_TTL_MS,
      });
    });
  }, [bridge]);

  return null;
}
